import React, { useRef, useState, useEffect } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { X, RotateCcw, Check } from 'lucide-react';
import { useLegacyStorage } from '../../hooks/useLegacyStorage';
import { compressSignatureSync } from '../../utils/imageCompression';

// UNIFIED storage key for ALL modules
const SHARED_SIGNER_KEY = 'sms_authorized_signer';
const LEGACY_HOR_KEY = 'hor_authorized_person';  // For migration

const SignatureModal = ({
  isOpen,
  onClose,
  onSave,
  signatureType = 'individual',  // 'individual' (crew, assessor) or 'authorized' (master, supervisor)
  title = 'Signature',
  description = 'Please sign below.',
  existingSignature = null,
  existingName = '',
  existingPosition = ''
}) => {
  const sigCanvas = useRef(null);
  const [isEmpty, setIsEmpty] = useState(true);
  const [signerName, setSignerName] = useState('');
  const [signerPosition, setSignerPosition] = useState('');
  const [rememberSigner, setRememberSigner] = useState(false);
  const storage = useLegacyStorage();

  // Load saved signer details OR existing signature
  useEffect(() => {
    if (!isOpen) return;

    // Reset state
    setIsEmpty(true);
    setSignerName(existingName || '');
    setSignerPosition(existingPosition || '');

    // For authorized person, try to load saved details
    if (signatureType === 'authorized') {
      // Try new unified key first
      let savedSigner = storage.get(SHARED_SIGNER_KEY, null);

      // Migration: if no unified key but legacy HOR key exists, migrate it
      if (!savedSigner) {
        const legacyHor = storage.get(LEGACY_HOR_KEY, null);
        if (legacyHor) {
          savedSigner = legacyHor;
          // Migrate to new key
          storage.set(SHARED_SIGNER_KEY, legacyHor);
          storage.remove(LEGACY_HOR_KEY);
          console.log('[SignatureModal] Migrated hor_authorized_person to sms_authorized_signer');
        }
      }

      if (savedSigner) {
        setSignerName(savedSigner.name || '');
        setSignerPosition(savedSigner.position || savedSigner.role || '');
        setRememberSigner(true);

        // Load saved signature if available
        if (savedSigner.signature && sigCanvas.current) {
          setTimeout(() => {
            sigCanvas.current.fromDataURL(savedSigner.signature);
            setIsEmpty(false);
          }, 100);
        }
      }
    }

    // Load existing signature if editing
    if (existingSignature && sigCanvas.current) {
      setTimeout(() => {
        sigCanvas.current.fromDataURL(existingSignature);
        setIsEmpty(false);
      }, 100);
    }
  }, [isOpen, signatureType, existingSignature, existingName, existingPosition, storage]);

  const handleClear = () => {
    sigCanvas.current.clear();
    setIsEmpty(true);
  };

  const handleSave = () => {
    if (sigCanvas.current.isEmpty()) return;
    if (!signerName.trim()) {
      alert('Please enter your name');
      return;
    }

    // FT-129: Compress signature (resize to 300x100, white bg, JPEG 60%)
    const signatureData = compressSignatureSync(sigCanvas);

    // Save authorized signer details if remember checkbox is checked
    if (signatureType === 'authorized' && rememberSigner) {
      storage.set(SHARED_SIGNER_KEY, {
        name: signerName,
        position: signerPosition,
        signature: signatureData
      });
    } else if (signatureType === 'authorized' && !rememberSigner) {
      // Clear saved details if unchecked
      storage.remove(SHARED_SIGNER_KEY);
    }

    // Return signature object with name and timestamp
    onSave({
      data: signatureData,
      name: signerName,
      position: signerPosition,
      timestamp: Date.now()
    }, signatureType);

    handleClear();
    if (!rememberSigner) {
      setSignerName('');
      setSignerPosition('');
    }
    onClose();
  };

  const handleBegin = () => {
    setIsEmpty(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black bg-opacity-25" onClick={onClose} />

      <div className="relative min-h-screen flex items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">{title}</h3>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          <div className="p-6">
            <p className="text-sm text-gray-600 mb-4">{description}</p>

            {/* Name and Position inputs */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={signerName}
                  onChange={(e) => setSignerName(e.target.value)}
                  placeholder="Enter your full name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Position
                </label>
                <input
                  type="text"
                  value={signerPosition}
                  onChange={(e) => setSignerPosition(e.target.value)}
                  placeholder="e.g., Master, Supervisor"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                />
              </div>
            </div>

            {/* Remember checkbox - only for authorized type */}
            {signatureType === 'authorized' && (
              <div className="mb-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={rememberSigner}
                    onChange={(e) => setRememberSigner(e.target.checked)}
                    className="h-4 w-4 text-cyan-600 focus:ring-cyan-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    Remember my signature for all modules
                  </span>
                </label>
                {rememberSigner && storage.get(SHARED_SIGNER_KEY, null) && (
                  <p className="mt-1 text-xs text-blue-600">
                    ✓ Using saved signature (works across Familiarisation, Hours of Rest, Risk Assessments, etc.)
                  </p>
                )}
              </div>
            )}

            {/* Signature canvas */}
            <div className="border-2 border-gray-300 rounded-lg bg-gray-50">
              <SignatureCanvas
                ref={sigCanvas}
                penColor="black"
                canvasProps={{
                  width: 600,
                  height: 200,
                  className: 'signature-canvas w-full'
                }}
                onBegin={handleBegin}
              />
            </div>

            <div className="mt-4 flex items-center justify-between">
              <button
                onClick={handleClear}
                disabled={isEmpty}
                className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                Clear
              </button>

              <div className="space-x-3">
                <button
                  onClick={onClose}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={isEmpty || !signerName.trim()}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Check className="h-4 w-4 mr-2" />
                  Save Signature
                </button>
              </div>
            </div>

            <div className="mt-4 text-xs text-gray-500">
              <p>By signing, you confirm that this constitutes a legal signature under maritime regulations.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignatureModal;
